import React, { useEffect, useRef, useState } from "react";
import RefreshIcon from "@mui/icons-material/Refresh";

/// A count whose own footprint is the way back from a stale list: on hover it
/// becomes a refresh button, so a header that fetches once — the org roster, a
/// Shared-with-me page whose spaces never poll — gains an affordance without
/// gaining a control. Font size is inherited so the count sits at whatever
/// scale its header uses.
export const RefreshableCount: React.FC<{
  label: string;
  /// Names what gets refreshed, for the label and the tooltip ("members",
  /// "shared items").
  subject: string;
  onRefresh: () => Promise<void> | void;
  /// Minimum gap between refreshes — a stale list is a nuisance, not an
  /// emergency, and the button sits under the pointer for a whole read.
  cooldownMs?: number;
}> = ({ label, subject, onRefresh, cooldownMs = 5000 }) => {
  const [spinning, setSpinning] = useState(false);
  const [cooling, setCooling] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const refresh = async () => {
    if (spinning || cooling) return;
    setSpinning(true);
    try {
      await onRefresh();
    } finally {
      setSpinning(false);
      setCooling(true);
      timer.current = setTimeout(() => setCooling(false), cooldownMs);
    }
  };

  const busy = spinning || cooling;

  return (
    <button
      type="button"
      onClick={() => void refresh()}
      disabled={busy}
      aria-label={`Refresh ${subject}`}
      title={cooling ? "Just refreshed" : `Refresh ${subject}`}
      className="group/count relative inline-flex items-center justify-center font-mono text-fg-muted hover:text-fg disabled:hover:text-fg-muted"
      style={{ fontSize: "inherit", fontVariantNumeric: "tabular-nums" }}
    >
      {/* The label keeps the width; the icon takes its place, so nothing in
          the header shifts when the pointer arrives. */}
      <span className={spinning ? "invisible" : "group-hover/count:invisible"}>
        {label}
      </span>
      <span
        className={`absolute inset-0 flex items-center justify-center ${
          spinning ? "" : "opacity-0 group-hover/count:opacity-100"
        }`}
      >
        <RefreshIcon
          sx={{ fontSize: 12 }}
          className={spinning ? "animate-spin" : ""}
        />
      </span>
    </button>
  );
};
